import type { useAuth } from "@clerk/react";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button,
} from "@ndd/ui";
import { useEffect, useState, type ReactElement } from "react";
import type { RichTextDocument } from "../../../../shared/rich-text-document";
import type {
  AppointmentDraftGenerationUi,
  AppointmentEnhanceActionState,
} from "@/lib/appointment-documentation-state";
import { deviceTimezone } from "@/lib/appointments";
import { draftBodyControls } from "@/lib/appointment-workspace-state";
import {
  presentedSoapDraftDelivery,
  type SoapDraftDelivery,
} from "@/lib/soap-draft-delivery";
import { RichTextEditor } from "@/components/v2/rich-text-editor";
import { useFormDirtyGuard } from "@/components/v2/use-form-dirty-guard";
import {
  AppointmentDraftSection,
  DraftCard,
  DraftPlaceholder,
  LockedDraftPreview,
} from "./appointment-draft-section";
import { EditorConflictBanner, EditorSaveStatus } from "./editor-status";
import { SoapDraftFooter } from "./soap-draft-footer";
import {
  useSoapDraftAutosave,
  type SoapDraftApprovalActionOutcome,
  type SoapDraftClientTarget,
} from "./use-appointment-draft-autosave";
import { CopyDraftButton } from "./copy-draft-button";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

/**
 * The Enhanced SOAP draft tab. Generation phases stay owned by
 * `AppointmentDraftSection`; once one persisted Draft revision exists this
 * mounts the review surface: the editor, autosave, and the footer that holds
 * provenance, Copy, and Approve.
 */
export function AppointmentDraftEditor({
  appointmentId,
  generation,
  enhanceAction,
  delivery,
  reconnecting,
  onRetry,
  onApproved,
  getToken,
  reEnhancing = false,
  onShownRevision,
}: {
  appointmentId: string;
  generation: AppointmentDraftGenerationUi;
  enhanceAction: AppointmentEnhanceActionState;
  /** Healthie delivery state of the current approval, from the polled snapshot. */
  delivery: SoapDraftDelivery;
  /** The snapshot store is reconnecting, so `delivery` is the last known state. */
  reconnecting: boolean;
  onRetry: () => void;
  /**
   * A successful approval asks the snapshot store for an immediate re-read;
   * the promise settles once that read has landed.
   */
  onApproved: () => Promise<void>;
  getToken: ClerkGetToken;
  /**
   * NO-785: a Re-enhance is running. The head stays on screen read-only:
   * editing, autosave, and Approve all wait until the run ends.
   */
  reEnhancing?: boolean;
  /** NO-785: reports the revision the editor has loaded (null when none). */
  onShownRevision?: (revisionId: string | null) => void;
}): ReactElement | null {
  if (generation.phase !== "draft_ready") {
    return (
      <AppointmentDraftSection
        generation={generation}
        enhanceAction={enhanceAction}
        onRetry={onRetry}
      />
    );
  }

  return (
    <SoapDraftReview
      target={{ kind: "appointment", id: appointmentId }}
      lockedPreview={generation.streamedDraftText}
      ready
      readOnly={reEnhancing}
      onShownRevision={onShownRevision}
      delivery={delivery}
      reconnecting={reconnecting}
      onApproved={onApproved}
      getToken={getToken}
    />
  );
}

/**
 * Review and edit one persisted Draft. A Draft that exists but cannot be
 * edited renders read-only with a plain explanation instead of an editor,
 * so a generated Version the editor cannot mount degrades to "look, don't
 * touch" rather than to a crash or a coerced document. Adopting a newer
 * revision and leaving with unsaved text never happen silently.
 */
export function SoapDraftReview({
  target,
  lockedPreview,
  ready,
  readOnly = false,
  onShownRevision,
  delivery,
  reconnecting = false,
  onApproved,
  getToken,
}: {
  target: SoapDraftClientTarget;
  /** NO-772: the streamed draft, shown locked until the saved Draft loads. */
  lockedPreview?: string;
  ready: boolean;
  /** NO-785: show the head without letting it change (a Re-enhance runs). */
  readOnly?: boolean;
  /** NO-785: told which persisted revision this editor has loaded. */
  onShownRevision?: (revisionId: string | null) => void;
  /** Absent on a surface without a documentation snapshot (Appointmentless Session). */
  delivery?: SoapDraftDelivery;
  reconnecting?: boolean;
  onApproved?: () => Promise<void>;
  getToken: ClerkGetToken;
}): ReactElement {
  const draft = useSoapDraftAutosave({ target, ready, getToken });
  const dirtyGuard = useFormDirtyGuard(draft.snapshot.dirty);
  const [now] = useState(() => new Date().toISOString());
  const timezone = deviceTimezone();
  const lock = useDraftBodyLock(draft.persistedDraft?.review.state, readOnly);
  useReportShownRevision(draft.persistedDraft?.revision.id ?? null, onShownRevision);
  const presentedDelivery = useApprovalDelivery(delivery, onApproved);
  const appointmentId = target.kind === "appointment" ? target.id : undefined;

  // A Draft that stops being editable mid-session keeps the editor mounted
  // with the RD's unsaved text; only a Draft that was never editable renders
  // the notice instead. `unsavedDocument` is absent exactly in that second
  // case.
  const editableDocument = editableDocumentFor(draft.state);

  if (editableDocument === undefined || draft.persistedDraft === undefined) {
    return (
      <DraftCard>
        <DraftNotice
          state={draft.state}
          lockedPreview={lockedPreview}
          onReload={draft.reload}
        />
      </DraftCard>
    );
  }

  return (
    <>
      <SoapDraftEditorContent
        appointmentId={appointmentId}
        draft={draft}
        editableDocument={editableDocument}
        locked={lock.editorDisabled}
        approveBlocked={lock.approveBlocked}
        now={now}
        timezone={timezone}
        delivery={presentedDelivery.delivery}
        reconnecting={reconnecting}
        onApprove={() => presentedDelivery.approveWith(draft.approveCurrent)}
        onUnlock={lock.unlock}
      />
      <DraftDiscardDialog dirtyGuard={dirtyGuard} />
    </>
  );
}

/**
 * An approved draft's body is locked until "Make changes"; a fresh approval
 * locks it again, so the unlock is per approval. NO-785: a running Re-enhance
 * locks it too, whatever the review state.
 */
/** NO-785: keeps the workspace told which revision this editor shows. */
function useReportShownRevision(
  revisionId: string | null,
  report: ((revisionId: string | null) => void) | undefined
): void {
  useEffect(() => {
    report?.(revisionId);

    return () => report?.(null);
  }, [revisionId, report]);
}

function useDraftBodyLock(
  reviewState: "draft" | "approved" | undefined,
  readOnly: boolean
): ReturnType<typeof draftBodyControls> & { unlock: () => void } {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (reviewState === "draft") setUnlocked(false);
  }, [reviewState]);

  return {
    ...draftBodyControls({ reviewState, unlocked, readOnly }),
    unlock: () => setUnlocked(true),
  };
}

type SoapDraftController = ReturnType<typeof useSoapDraftAutosave>;
type DirtyGuard = ReturnType<typeof useFormDirtyGuard>;

/**
 * Between a successful approval and the snapshot read it triggers, the polled
 * `delivery` is stale (it predates the approval), so the surface shows
 * "sending" instead. The wait ends only when that read SUCCEEDS — never on a
 * timer and never on a failed read — so a status outage right after approval
 * keeps "sending" on screen (with the reconnecting cue) instead of surfacing
 * the stale "not sent" line, and an environment without writeback settles on
 * the snapshot's honest `null` as soon as a read lands.
 */
function useApprovalDelivery(
  delivery: SoapDraftDelivery | undefined,
  onApproved: (() => Promise<void>) | undefined
): {
  delivery: SoapDraftDelivery | undefined;
  /** Runs the approval, then holds "sending" until the triggered read succeeds. */
  approveWith: (
    approveCurrent: SoapDraftController["approveCurrent"]
  ) => Promise<SoapDraftApprovalActionOutcome>;
} {
  const [awaitingRead, setAwaitingRead] = useState(false);

  async function approveWith(approveCurrent: SoapDraftController["approveCurrent"]) {
    const outcome = await approveCurrent();

    if (outcome === "approved") {
      setAwaitingRead(true);

      try {
        await onApproved?.();
      } finally {
        setAwaitingRead(false);
      }
    }

    return outcome;
  }

  return {
    delivery:
      delivery === undefined
        ? undefined
        : presentedSoapDraftDelivery(delivery, awaitingRead),
    approveWith,
  };
}

function editableDocumentFor(
  state: SoapDraftController["state"]
): RichTextDocument | undefined {
  if (state.status === "editable") return state.initialDocument;

  if (state.status === "not_editable") return state.unsavedDocument;

  return undefined;
}

function SoapDraftEditorContent({
  appointmentId,
  draft,
  editableDocument,
  locked,
  approveBlocked,
  now,
  timezone,
  delivery,
  reconnecting,
  onApprove,
  onUnlock,
}: {
  /** Absent on the Appointmentless Session Draft, which has no Healthie note link. */
  appointmentId: string | undefined;
  draft: SoapDraftController;
  editableDocument: RichTextDocument;
  locked: boolean;
  approveBlocked: boolean;
  now: string;
  timezone: string;
  delivery: SoapDraftDelivery | undefined;
  reconnecting: boolean;
  onApprove: SoapDraftController["approveCurrent"];
  onUnlock: () => void;
}): ReactElement {
  return (
    <div aria-label="SOAP Draft editor" className="flex min-h-0 flex-1 flex-col">
      <RichTextEditor
        initialValue={editableDocument}
        replacement={draft.replacement}
        disabled={locked}
        shouldFillAvailableHeight
        onChange={draft.edit}
        toolbarEnd={
          // A running Re-enhance locks the body but is not an approval.
          <DraftSaveStatus draft={draft} locked={locked && !approveBlocked} />
        }
        alert={<DraftEditorAlert draft={draft} />}
        footer={
          <DraftEditorFooter
            appointmentId={appointmentId}
            draft={draft}
            locked={locked}
            approveBlocked={approveBlocked}
            now={now}
            timezone={timezone}
            delivery={delivery}
            reconnecting={reconnecting}
            onApprove={onApprove}
            onUnlock={onUnlock}
          />
        }
      />
    </div>
  );
}

function DraftSaveStatus({
  draft,
  locked,
}: {
  draft: SoapDraftController;
  locked: boolean;
}): ReactElement {
  return (
    <EditorSaveStatus
      status={draft.snapshot.status}
      savedLabel="Draft saved just now"
      locked={locked}
      onRetry={draft.retry}
    />
  );
}

function DraftEditorAlert({
  draft,
}: {
  draft: SoapDraftController;
}): ReactElement | undefined {
  if (draft.snapshot.status === "conflict") {
    return (
      <EditorConflictBanner
        subject="draft"
        onUseLatest={draft.useLatest}
        onSaveMine={draft.saveMine}
      />
    );
  }

  if (draft.snapshot.status !== "not_editable") return undefined;

  return (
    <Alert variant="danger" className="shrink-0 rounded-none border-x-0 border-t-0">
      <AlertTitle>This draft can no longer be edited.</AlertTitle>
      <AlertDescription className="mt-1">
        Your text is still on screen, but new changes won&apos;t save. Select and copy
        any unsaved text by hand before leaving. Copy note excludes unsaved changes.
      </AlertDescription>
    </Alert>
  );
}

function DraftEditorFooter({
  appointmentId,
  draft,
  locked,
  approveBlocked,
  now,
  timezone,
  delivery,
  reconnecting,
  onApprove,
  onUnlock,
}: {
  appointmentId: string | undefined;
  draft: SoapDraftController;
  locked: boolean;
  approveBlocked: boolean;
  now: string;
  timezone: string;
  delivery: SoapDraftDelivery | undefined;
  reconnecting: boolean;
  onApprove: SoapDraftController["approveCurrent"];
  onUnlock: () => void;
}): ReactElement {
  const copyDisabled = ["conflict", "not_editable"].includes(draft.snapshot.status);

  return (
    <SoapDraftFooter
      {...(appointmentId === undefined ? {} : { appointmentId })}
      review={draft.persistedDraft!.review}
      provenance={draft.persistedDraft!.provenance}
      snapshot={draft.snapshot}
      approving={draft.approving}
      locked={locked}
      approveBlocked={approveBlocked}
      now={now}
      timezone={timezone}
      copyAction={
        <CopyDraftButton disabled={copyDisabled} copyCurrent={draft.copyCurrent} />
      }
      delivery={delivery}
      reconnecting={reconnecting}
      onApprove={onApprove}
      onUnlock={onUnlock}
    />
  );
}

function DraftDiscardDialog({ dirtyGuard }: { dirtyGuard: DirtyGuard }): ReactElement {
  return (
    <AlertDialog
      open={dirtyGuard.showDiscardDialog}
      onOpenChange={dirtyGuard.handleDiscardDialogOpenChange}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave before this draft is saved?</AlertDialogTitle>
          <AlertDialogDescription>
            The latest draft changes could be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => dirtyGuard.confirmDiscard(ignoreDiscardResult)}
          >
            Leave without saving
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ignoreDiscardResult(): void {}

function DraftNotice({
  state,
  lockedPreview,
  onReload,
}: {
  state: ReturnType<typeof useSoapDraftAutosave>["state"];
  lockedPreview?: string;
  onReload: () => void;
}): ReactElement | null {
  if (state.status === "editable" || state.status === "unavailable") return null;

  if (state.status === "loading") {
    // NO-772: the streamed draft stays on screen, locked, until the Draft opens.
    return lockedPreview ? (
      <LockedDraftPreview markdown={lockedPreview} />
    ) : (
      <DraftPlaceholder label="Opening the draft…" />
    );
  }

  if (state.status === "not_editable") {
    return (
      <p role="status" className="px-5 py-4 text-body text-muted-foreground">
        {state.reason === "document_unsupported"
          ? "This draft can't be opened for editing yet. Nothing was changed."
          : "This draft isn't ready to edit yet."}
      </p>
    );
  }

  return (
    <Alert variant="danger" className="m-4">
      <AlertTitle>The draft didn&apos;t open.</AlertTitle>
      <AlertDescription className="mt-1">
        Nothing was changed. You can try again.
        <div className="mt-2">
          <Button type="button" variant="outline" size="sm" onClick={onReload}>
            Try again
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
